import { useCallback, useRef, useState } from 'react'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { ExistingAppointmentDialog, QuoteAppointmentDetails } from '@components/scheduler/existing-appointment/ExistingAppointmentDialog'
import { findExistingAppointment } from '@components/scheduler/existing-appointment/existingAppointment'

/**
 * CAR-4034 — one shared check in front of every New Appointment on a quote (Quote Summary
 * and Quote Queue), so the entry points can't drift apart.
 *
 *   const { requestNewAppointment, guardDialog } = useExistingAppointmentGuard(openForm)
 *   onClick={() => requestNewAppointment(quote)}   …   {guardDialog}
 *
 * No appointment → openForm(quote) straight away, as before. Otherwise the confirm dialog
 * shows first; Schedule Another calls openForm(quote) with the quote captured at click time.
 * Not gated on useIsReadOnly: client users may book.
 */
export const useExistingAppointmentGuard = (onCreateNew) => {
  const ccId = useCenterStore((store) => store.ccId)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [pending, setPending] = useState(null)
  const [details, setDetails] = useState(null)
  const checkingRef = useRef(false)

  const requestNewAppointment = useCallback(
    async (quote) => {
      if (!quote?.id || checkingRef.current) return
      checkingRef.current = true
      setLoading(true)
      try {
        const existing = await findExistingAppointment({ ccId, quote })
        if (existing) setPending({ ...existing, quote })
        else onCreateNew(quote)
      } finally {
        checkingRef.current = false
        setLoading(false)
      }
    },
    [ccId, onCreateNew, setLoading]
  )

  const openDetails = (appointmentId, editMode) => {
    setPending(null)
    setDetails({ appointmentId, editMode })
  }

  const guardDialog = (
    <>
      <ExistingAppointmentDialog
        existing={pending}
        quoteReference={pending?.quote?.reference_number}
        onCancel={() => setPending(null)}
        onScheduleAnother={() => {
          const { quote } = pending
          setPending(null)
          onCreateNew(quote)
        }}
        onView={(id) => openDetails(id, false)}
        onEdit={(id) => openDetails(id, true)}
      />
      {details && <QuoteAppointmentDetails {...details} onClose={() => setDetails(null)} />}
    </>
  )

  return { requestNewAppointment, guardDialog }
}
